import { Component } from '@theme/component';

/**
 * Доповнення до штатних фасетів Horizon: пошук усередині фасета, згортання
 * списку значень і двоповзунковий слайдер ціни.
 *
 * Уся мережева робота (Section Rendering API, pushState, AbortController)
 * лишається за `facets-form-component` з `facets.js` — тут ми лише кличемо його
 * публічні методи `updateFilters()` / `updateFiltersByURL()`.
 */

const DEBOUNCE_MS = 300;

/** Запасний крок «Показати більше», якщо в розмітці немає data-step. */
const DEFAULT_MORE_STEP = 10;

/**
 * @param {(...args: any[]) => void} fn
 * @param {number} wait
 */
function debounce(fn, wait) {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timer;
  return (/** @type {any[]} */ ...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

/**
 * Затемнює сітку результатів на час запиту. Клас знімається сам, коли секція
 * перерендериться і замінить розмітку.
 * @param {Element} origin
 */
function markBusy(origin) {
  const search = origin.closest('.bkv-search');
  // У режимі індексу каталогу затемненням керує bkv-catalog.js. Тут його ставити
  // не можна: слухач `change` на document спрацьовує ПІСЛЯ обробника компонента,
  // а коли картки вже в кеші, bkv-catalog.js встигає намалювати і зняти клас на
  // мікрозадачах між цими двома слухачами — і сітка лишалась сірою назавжди
  // (сортування «ціна» → знову «релевантність»).
  const catalog = search?.getAttribute('data-bkv-catalog');
  if (catalog && catalog !== 'off') return;

  const results = search?.querySelector('.bkv-results');
  if (!results) return;
  results.classList.add('bkv-results--busy');
  results.setAttribute('aria-busy', 'true');
}

/**
 * Тіло одного списковго фасета: клієнтський пошук по значеннях і «Ще N …».
 *
 * @extends {Component<{ search?: HTMLInputElement, moreButton?: HTMLButtonElement, noMatches?: HTMLElement }>}
 */
class BkvFacetPanel extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.refs.search?.addEventListener('input', this.#onSearch);
    this.refs.moreButton?.addEventListener('click', this.#toggleMore);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.search?.removeEventListener('input', this.#onSearch);
    this.refs.moreButton?.removeEventListener('click', this.#toggleMore);
  }

  /** Скільки значень додає один клік; задається в налаштуваннях секції. */
  get #step() {
    const step = Number(this.refs.moreButton?.dataset.step);
    return Number.isFinite(step) && step > 0 ? step : DEFAULT_MORE_STEP;
  }

  /** @returns {HTMLElement[]} Значення, приховані згортанням. */
  get #extras() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('.bkv-facet__value--extra'))
    );
  }

  /** @returns {HTMLElement[]} «Зайві» значення, які ще не розкриті кроками. */
  get #collapsed() {
    return this.#extras.filter((item) => !item.hasAttribute('data-revealed'));
  }

  #toggleMore = () => {
    const collapsed = this.#collapsed;

    if (collapsed.length === 0) {
      // Список розкрито повністю — цей самий клік згортає його назад.
      for (const item of this.#extras) {
        item.removeAttribute('data-revealed');
        // Під час активного пошуку видимість вирішує фільтр, а не згортання.
        if (!item.hasAttribute('data-search-hidden')) item.setAttribute('hidden', '');
      }
    } else {
      for (const item of collapsed.slice(0, this.#step)) {
        item.setAttribute('data-revealed', '');
        if (!item.hasAttribute('data-search-hidden')) item.removeAttribute('hidden');
      }
    }

    this.#syncMoreButton();
  };

  /** Кнопка показує, скільки значень додасть наступний клік. */
  #syncMoreButton() {
    const { moreButton } = this.refs;
    if (!moreButton) return;

    const remaining = this.#collapsed.length;
    moreButton.setAttribute('aria-expanded', String(remaining === 0));
    moreButton.textContent =
      remaining === 0
        ? moreButton.dataset.lessLabel ?? ''
        : `${moreButton.dataset.moreLabel ?? ''} (${Math.min(remaining, this.#step)})`;
  }

  #onSearch = () => {
    const { search, moreButton, noMatches } = this.refs;
    if (!search) return;

    const query = search.value.trim().toLowerCase();
    const items = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('.bkv-facet__value'))
    );

    let matches = 0;

    for (const item of items) {
      const label = item.dataset.label ?? '';
      const hit = query === '' || label.includes(query);
      if (hit) matches += 1;

      if (query === '') {
        // Повертаємо звичайний режим: приховані лише «зайві» значення,
        // і лише поки список згорнутий.
        item.removeAttribute('data-search-hidden');
        const stillCollapsed =
          item.classList.contains('bkv-facet__value--extra') && !item.hasAttribute('data-revealed');
        item.toggleAttribute('hidden', stillCollapsed);
      } else {
        // Під час пошуку показуємо всі збіги, включно із згорнутими.
        item.toggleAttribute('data-search-hidden', !hit);
        item.toggleAttribute('hidden', !hit);
      }
    }

    // Кнопка згортання не має сенсу, поки список відфільтровано.
    moreButton?.toggleAttribute('hidden', query !== '');
    noMatches?.toggleAttribute('hidden', matches !== 0);
  };
}

if (!customElements.get('bkv-facet-panel')) {
  customElements.define('bkv-facet-panel', BkvFacetPanel);
}

/**
 * Фасет ціни: слайдер керує числовими полями, вони — джерелом істини для форми.
 *
 * @extends {Component<{ minInput?: HTMLInputElement, maxInput?: HTMLInputElement, minRange?: HTMLInputElement, maxRange?: HTMLInputElement, fill?: HTMLElement }>}
 */
class BkvPriceFacet extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { minInput, maxInput, minRange, maxRange } = this.refs;

    minInput?.addEventListener('input', this.#onNumberInput);
    maxInput?.addEventListener('input', this.#onNumberInput);
    minRange?.addEventListener('input', this.#onRangeInput);
    maxRange?.addEventListener('input', this.#onRangeInput);

    this.#paintFill();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { minInput, maxInput, minRange, maxRange } = this.refs;
    minInput?.removeEventListener('input', this.#onNumberInput);
    maxInput?.removeEventListener('input', this.#onNumberInput);
    minRange?.removeEventListener('input', this.#onRangeInput);
    maxRange?.removeEventListener('input', this.#onRangeInput);
  }

  get #rangeMax() {
    return Number(this.dataset.rangeMax ?? 0);
  }

  /** Значення полів у мажорних одиницях, вже приведені до валідного діапазону. */
  get #values() {
    const { minInput, maxInput } = this.refs;
    const max = this.#rangeMax;

    const rawMin = minInput?.value === '' ? 0 : Number(minInput?.value);
    const rawMax = maxInput?.value === '' ? max : Number(maxInput?.value);

    const lo = Number.isFinite(rawMin) ? Math.min(Math.max(rawMin, 0), max) : 0;
    const hi = Number.isFinite(rawMax) ? Math.min(Math.max(rawMax, 0), max) : max;

    // Не даємо повзункам перетнутись.
    return lo > hi ? { lo: hi, hi: lo } : { lo, hi };
  }

  #onNumberInput = () => {
    const { minRange, maxRange } = this.refs;
    const { lo, hi } = this.#values;

    if (minRange) minRange.value = String(lo);
    if (maxRange) maxRange.value = String(hi);

    this.#paintFill();
    this.#submit();
  };

  #onRangeInput = () => {
    const { minInput, maxInput, minRange, maxRange } = this.refs;
    const max = this.#rangeMax;

    let lo = Number(minRange?.value ?? 0);
    let hi = Number(maxRange?.value ?? max);
    if (lo > hi) [lo, hi] = [hi, lo];

    // Порожнє поле означає «межі немає» — не записуємо в URL зайвий параметр.
    if (minInput) minInput.value = lo === 0 ? '' : String(lo);
    if (maxInput) maxInput.value = hi === max ? '' : String(hi);

    this.#paintFill();
    this.#submit();
  };

  #paintFill() {
    const { fill } = this.refs;
    const max = this.#rangeMax;
    if (!fill || max <= 0) return;

    const { lo, hi } = this.#values;
    fill.style.insetInlineStart = `${(lo / max) * 100}%`;
    fill.style.insetInlineEnd = `${100 - (hi / max) * 100}%`;
  }

  #submit = debounce(() => {
    const form = this.closest('facets-form-component');
    if (!form || typeof (/** @type {any} */ (form).updateFilters) !== 'function') return;

    markBusy(this);
    /** @type {any} */ (form).updateFilters();
  }, DEBOUNCE_MS);
}

if (!customElements.get('bkv-price-facet')) {
  customElements.define('bkv-price-facet', BkvPriceFacet);
}

/**
 * Чіпи активних фільтрів і «Скинути все» — справжні посилання, тож без JS вони
 * просто ведуть на потрібний URL. Тут перехоплюємо клік, щоб оновити секцію без
 * перезавантаження.
 */
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const link = event.target.closest('.bkv-chip, .bkv-chips__clear');
  if (!(link instanceof HTMLAnchorElement)) return;

  // Не заважаємо відкрити в новій вкладці.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

  const form = link.closest('facets-form-component');
  if (!form || typeof (/** @type {any} */ (form).updateFiltersByURL) !== 'function') return;

  event.preventDefault();
  markBusy(link);
  /** @type {any} */ (form).updateFiltersByURL(link.href);
});

/**
 * Затемнення сітки на час запиту при кліку по чекбоксу фасета або сортуванню.
 */
document.addEventListener('change', (event) => {
  if (!(event.target instanceof Element)) return;
  if (!event.target.closest('.bkv-facets, .bkv-sort')) return;
  markBusy(event.target);
});

/*
 * Горизонтальна панель фасетів (Placement → «Horizontal bar on top»): кожен
 * фасет — кнопка з дропдауном поверх сітки. Відкритий щонайбільше один.
 *
 * Після кожного кліку по значенню фасети перемальовуються: штатний шлях —
 * morph, який знімає `open` (у відповіді сервера всі фасети закриті), індекс
 * каталогу — `innerHTML`, тобто нові елементи. Тому відкритий фасет пам'ятаємо
 * за id (він стабільний між рендерами) і повертаємо `open`, прокрутку списку й
 * фокус у MutationObserver — колбек спрацьовує мікрозадачею, до відмальовки,
 * тож дропдаун не блимає.
 */
const TOP_COL = '.bkv-search--facets-top .bkv-facets-col';
const desktop = matchMedia('(min-width: 990px)');

/** @type {{ id: string | null, scroll: number, focus: string | null }} */
const dropdown = { id: null, scroll: 0, focus: null };

/** @param {EventTarget | null} target */
function topFacetOf(target) {
  return target instanceof Element ? target.closest(`${TOP_COL} .bkv-facet`) : null;
}

/**
 * Дропдаун біля правого краю панелі вилазив би за екран — тоді вирівнюємо його
 * по правому краю кнопки. На мобільному панель на всю ширину, рахувати нічого.
 * @param {HTMLDetailsElement} details
 */
function alignDropdown(details) {
  details.classList.remove('bkv-facet--end');
  if (!desktop.matches) return;
  const panel = details.querySelector('.bkv-facet__body');
  const col = details.closest('.bkv-facets-col');
  if (!panel || !col) return;
  if (panel.getBoundingClientRect().right > col.getBoundingClientRect().right) {
    details.classList.add('bkv-facet--end');
  }
}

/** @param {HTMLDetailsElement | null} details */
function setDropdown(details) {
  for (const open of document.querySelectorAll(`${TOP_COL} .bkv-facet[open]`)) {
    if (open !== details) /** @type {HTMLDetailsElement} */ (open).open = false;
  }
  dropdown.id = details?.id || null;
  dropdown.scroll = 0;
  dropdown.focus = null;
  if (!details) return;
  details.open = true;
  alignDropdown(details);
}

/** Повертає відкритий дропдаун після перерендеру фасетів. */
function restoreDropdown() {
  if (!dropdown.id) return;
  const details = /** @type {HTMLDetailsElement | null} */ (document.getElementById(dropdown.id));
  // Фасет зник із нової видачі (значень не лишилось) — забуваємо його.
  if (!details || !details.matches(`${TOP_COL} .bkv-facet`)) {
    dropdown.id = null;
    return;
  }
  if (!details.open) details.open = true;
  if (!details.classList.contains('bkv-facet--end')) alignDropdown(details);

  const panel = details.querySelector('.bkv-facet__body');
  if (panel && panel.scrollTop !== dropdown.scroll) panel.scrollTop = dropdown.scroll;

  const active = document.activeElement;
  if (dropdown.focus && (!active || active === document.body)) {
    document.getElementById(dropdown.focus)?.focus({ preventScroll: true });
  }
}

const dropdownObserver = new MutationObserver(restoreDropdown);

function observeTopBars() {
  dropdownObserver.disconnect();
  for (const col of document.querySelectorAll(TOP_COL)) {
    dropdownObserver.observe(col, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });
  }
}

observeTopBars();
document.addEventListener('shopify:section:load', observeTopBars);

// Кнопка фасета: перемикаємо самі, щоб закрити сусідній.
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const summary = event.target.closest('.bkv-facet__summary');
  const details = topFacetOf(summary);
  if (!summary || !(details instanceof HTMLDetailsElement)) return;
  event.preventDefault();
  setDropdown(details.open ? null : details);
});

// Клік повз відкритий дропдаун закриває його.
document.addEventListener('pointerdown', (event) => {
  if (!dropdown.id) return;
  const open = document.getElementById(dropdown.id);
  if (open && event.target instanceof Node && open.contains(event.target)) return;
  setDropdown(null);
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !dropdown.id) return;
  const open = document.getElementById(dropdown.id);
  setDropdown(null);
  open?.querySelector('summary')?.focus();
});

document.addEventListener(
  'scroll',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.matches('.bkv-facet__body')) return;
    if (topFacetOf(target)?.id === dropdown.id) dropdown.scroll = target.scrollTop;
  },
  true
);

document.addEventListener('focusin', (event) => {
  const details = topFacetOf(event.target);
  if (details && details.id === dropdown.id && event.target instanceof Element && event.target.id) {
    dropdown.focus = event.target.id;
  }
});

// Розкладка дропдауна на мобільному й десктопі різна — при зміні закриваємо.
desktop.addEventListener('change', () => setDropdown(null));
